import { supabase as _supabase } from '../lib/supabase';
import { computeSplitSizes, DEFAULT_ERS } from '../lib/splits';
import type { ReserveRebalanceSummary } from '../lib/reserveRebalance';

// Cast to any to allow accessing tables not yet in generated Database types
// (session_splits, session_entries, notifications, event_cars, event_stewards, etc.)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const supabase = _supabase as any;

// Categorias de transmissão — não participam da divisão (espelha backend)
const BROADCASTER_CATS = new Set(['BROADCASTER', 'TRANSMISSÃO', 'TRANSMISSAO', 'NARRADOR']);


export interface EventRegisterPayload {
  car_id?: string | null;
  event_category_id?: string | null;
  car_name?: string | null;
  category_name?: string | null;
}

export interface RegistrationUpdatePayload {
  event_team_id?: string | null;
  car_id?: string | null;
  status?: string | null;
  event_category_id?: string | null;
  category_name?: string | null;
  car_name?: string | null;
}

export interface ActivateReservePayload {
  replace_pilot_id?: string | null;
  replace_registration_id?: string | null;
}

/**
 * Retorna o usuário autenticado atual tentando:
 * 1. Sessão ativa no cliente JS (auto-refreshes token se necessário)
 * 2. Chamada direta ao Supabase Auth
 * 3. Endpoint backend /users/me (cookie-based session)
 */
export async function getAuthenticatedUser(): Promise<{ id: string; email?: string } | null> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      return session.user;
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      return user;
    }
  } catch (err) {
    console.warn('[eventsService] Erro ao obter usuário via Supabase auth:', err);
  }

  try {
    const { usersApi } = await import('../lib/api');
    const meRes = await usersApi.me() as { user: { id: string; email?: string } };
    if (meRes?.user?.id) {
      return meRes.user;
    }
  } catch (err) {
    console.warn('[eventsService] Erro ao obter usuário via backend /users/me:', err);
  }

  return null;
}

/**
 * Fallback do reequilíbrio da fila de reserva (espelha `_rebalance_reserve_queue`
 * do backend). Usado apenas quando a API FastAPI está indisponível — por isso não
 * cobra/reembolsa marbles, mesmo comportamento do fallback de inscrição.
 *
 * - Promoção: FIFO pela ordem das inscrições enquanto houver vagas livres.
 * - Democção: quando a capacidade cai, os últimos a se inscrever voltam à reserva
 *   (pilotos já escalados em sessão são preservados).
 */
async function rebalanceReservesFallback(
  eventId: string,
  categoryId?: string,
  excludePilotIds?: string[]
): Promise<ReserveRebalanceSummary> {
  const summary: ReserveRebalanceSummary = { promoted: 0, demoted: 0, skipped_charge: [] };
  const excluded = new Set(excludePilotIds || []);
  try {
    const { data: event, error: evErr } = await supabase
      .from('events')
      .select('allow_reserves, registration_limit, status')
      .eq('id', eventId)
      .single();
    if (evErr || !event) return summary;

    const limit = Number(event.registration_limit) || 0;
    if (event.status === 'finished' || event.status === 'in_progress') return summary;
    if (!event.allow_reserves) return summary;

    const { count } = await supabase
      .from('event_registrations')
      .select('id', { count: 'exact', head: true })
      .eq('event_id', eventId)
      .eq('status', 'registered');
    let active = count || 0;

    // Orçamento: vagas globais livres; sem limite global, as vagas livres da
    // categoria que disparou o rebalance (espelha o backend).
    let budget: number | null = null;
    if (limit > 0) budget = limit - active;
    else if (categoryId) budget = await categoryFreeSlots(eventId, categoryId);
    if (budget === null) return summary;

    // ── Promoção: percorre a fila na ordem das inscrições ──────────────
    let promotedHere = 0;
    if (budget > 0) {
      const { data: queue } = await supabase
        .from('event_registrations')
        .select('id, pilot_id')
        .eq('event_id', eventId)
        .eq('status', 'reserve')
        .order('created_at', { ascending: true });

      for (const candidate of queue || []) {
        if (promotedHere >= budget) break;
        // Recém-rebaixado numa troca manual: não pode voltar imediatamente
        if (excluded.has(candidate.pilot_id)) continue;
        const { data: updated } = await supabase
          .from('event_registrations')
          .update({ status: 'registered', reserve_since: null })
          .eq('id', candidate.id)
          .eq('status', 'reserve')
          .select('id');
        if (!updated || updated.length === 0) continue;
        promotedHere += 1;
        active += 1;
        summary.promoted += 1;
      }
    }

    // ── Democção: os últimos a se inscrever perdem a vaga aberta ───────
    if (limit > 0 && active > limit) {
      const overflow = active - limit;
      const sessionedPilots = await pilotsWithSessionEntries(eventId);
      const { data: activeRegs } = await supabase
        .from('event_registrations')
        .select('id, pilot_id')
        .eq('event_id', eventId)
        .eq('status', 'registered')
        .order('created_at', { ascending: false });

      let demoted = 0;
      for (const candidate of activeRegs || []) {
        if (demoted >= overflow) break;
        if (sessionedPilots.has(candidate.pilot_id)) continue;

        const { data: updated } = await supabase
          .from('event_registrations')
          .update({ status: 'reserve', reserve_since: new Date().toISOString() })
          .eq('id', candidate.id)
          .eq('status', 'registered')
          .select('id');
        if (!updated || updated.length === 0) continue;
        demoted += 1;
        summary.demoted += 1;
      }
    }
  } catch (err) {
    console.warn('[rebalanceReservesFallback] Erro ao reequilibrar fila de reserva:', err);
  }
  return summary;
}

/** Pilotos já escalados em alguma sessão do evento (não devem ser rebaixados). */
async function pilotsWithSessionEntries(eventId: string): Promise<Set<string>> {
  try {
    const { data: days } = await supabase.from('event_days').select('id').eq('event_id', eventId);
    const dayIds = (days || []).map((d: any) => d.id);
    if (!dayIds.length) return new Set();

    const { data: races } = await supabase.from('races').select('id').in_('event_day_id', dayIds);
    const raceIds = (races || []).map((r: any) => r.id);
    if (!raceIds.length) return new Set();

    const { data: sessions } = await supabase.from('sessions').select('id').in_('race_id', raceIds);
    const sessionIds = (sessions || []).map((s: any) => s.id);
    if (!sessionIds.length) return new Set();

    const { data: entries } = await supabase
      .from('session_entries')
      .select('pilot_id')
      .in_('session_id', sessionIds);
    return new Set((entries || []).map((e: any) => e.pilot_id).filter(Boolean));
  } catch (err) {
    console.warn('[pilotsWithSessionEntries] Warning:', err);
    return new Set();
  }
}

/** Orçamento quando a categoria não tem limite (max_slots vazio = ilimitado). */
const UNLIMITED_BUDGET = 1e9;

/** Vagas livres de uma categoria: max_slots − confirmados nela (espelha o backend). */
async function categoryFreeSlots(eventId: string, categoryId: string): Promise<number | null> {
  try {
    const { data: cat, error } = await supabase
      .from('event_categories')
      .select('id, name, max_slots')
      .eq('id', categoryId)
      .eq('event_id', eventId)
      .maybeSingle();
    if (error || !cat) return null;

    const maxSlots = Number(cat.max_slots) || 0;
    if (maxSlots <= 0) return UNLIMITED_BUDGET;

    const { data: regs } = await supabase
      .from('event_registrations')
      .select('event_category_id, category_name, status')
      .eq('event_id', eventId);

    const catName = (cat.name || '').trim().toLowerCase();
    let activeInCat = 0;
    // Mesma regra do backend: id primeiro, category_name só como fallback de
    // inscrição sem event_category_id. Sem isso a inscrição contava nas DUAS
    // categorias e a vaga livre deixava de aparecer.
    for (const r of regs || []) {
      if (r.status && r.status !== 'registered') continue;
      if (r.event_category_id) {
        if (r.event_category_id === categoryId) activeInCat += 1;
      } else if (catName && (r.category_name || '').trim().toLowerCase() === catName) {
        activeInCat += 1;
      }
    }
    return Math.max(0, maxSlots - activeInCat);
  } catch (err) {
    console.warn('[categoryFreeSlots] Warning:', err);
    return null;
  }
}

export const eventsService = {
  /**
   * Retorna detalhes completos do evento.
   * Tenta primeiro o backend HTTP (eventsApi / service_role).
   * Utiliza a RPC get_event_details_v2_json como fallback.
   */
  async getById(eventId: string, userId?: string): Promise<any> {
    try {
      const { eventsApi } = await import('../lib/api');
      const backendEvent = await eventsApi.getById(eventId);
      if (backendEvent && backendEvent.id) {
        return backendEvent;
      }
    } catch (e) {
      console.warn('[eventsService.getById] Backend endpoint error, tentando RPC fallback:', e);
    }

    try {
      const { data, error } = await supabase.rpc('get_event_details_v2_json', {
        p_event_id: eventId,
        p_user_id: userId || null,
      });

      if (!error && data) {
        return data;
      }
    } catch (e) {
      console.warn('[eventsService.getById] RPC fallback:', e);
    }

    // Fallback: Query direta com joins relacionais no Supabase JS
    // OTIMIZADO: event_registrations removido do join principal para evitar puxar
    // todos os registros em eventos grandes. Usamos HEAD queries separadas para
    // counts e uma query filtrada por userId para verificar inscrição do user.
    const { data: event, error: eventErr } = await supabase
      .from('events')
      .select(`
        id, name, description, rules, status, scheduled_at, image_url,
        game_id, league_id, category_id, season_id,
        registration_limit, allow_reserves, has_splits, slots_per_split,
        registrations_locked, scoring_settings, commission_settings, commission_enabled,
        scoring_template, event_type, platform, broadcaster_id, is_paid, entry_fee_tokens,
        min_license_tier_id, max_license_tier_id, created_by, created_at, updated_at,
        voice_isolation_mode,
        leagues (id, name, slug, logo_url, owner_id, access_level, owner:users!leagues_owner_id_fkey(id, username, display_name, avatar_url)),
        created_by_user:users!events_created_by_fkey(id, username, display_name, avatar_url),
        games (id, name, short_name, slug),
        min_license_tier:license_tiers!events_min_license_tier_id_fkey(id, name, label, color_hex),
        max_license_tier:license_tiers!events_max_license_tier_id_fkey(id, name, label, color_hex),
        event_categories (id, name, tag, color_hex, max_slots)
      `)
      .eq('id', eventId)
      .single();

    if (eventErr || !event) {
      throw new Error(eventErr?.message || 'Evento não encontrado.');
    }

    // Busca inscrições com category_name para excluir BROADCASTERs da contagem
    const BROADCASTER_CATS = new Set(['BROADCASTER', 'TRANSMISSÃO', 'TRANSMISSAO', 'NARRADOR']);
    const { data: allRegs } = await supabase
      .from('event_registrations')
      .select('id, status, category_name')
      .eq('event_id', eventId)
      .or('status.eq.registered,status.is.null,status.eq.reserve');

    const regsData = allRegs || [];
    const activeCount = regsData.filter(
      r => !BROADCASTER_CATS.has((r.category_name || '').toUpperCase().trim())
        && (r.status === 'registered' || !r.status)
    ).length;
    const reserveCount = regsData.filter(r => r.status === 'reserve').length;

    let isRegistered = false;
    let userRegistrationStatus: string | null = null;
    let isReserve = false;

    // Verifica inscrição do user logado — query filtrada por userId (1 row no máximo)
    if (userId) {
      const { data: userReg } = await supabase
        .from('event_registrations')
        .select('id, status, event_category_id, category_name')
        .eq('event_id', eventId)
        .eq('pilot_id', userId)
        .maybeSingle();

      if (userReg) {
        isRegistered = true;
        userRegistrationStatus = userReg.status || 'registered';
        isReserve = userRegistrationStatus === 'reserve';
      }
    }

    return {
      ...event,
      registration_count: activeCount ?? 0,
      reserve_count: reserveCount ?? 0,
      estimated_sof: 0, // SoF calculado pelo backend; zero no fallback JS
      is_registered: isRegistered,
      user_registration_status: userRegistrationStatus,
      is_reserve: isReserve,
    };
  },

  /**
   * Retorna a estrutura hierárquica completa do evento (Dias, Corridas, Sessões, Splits, Entries).
   */
  async getStructure(eventId: string): Promise<any[]> {
    try {
      const { eventsApi } = await import('../lib/api');
      const backendStructure = await eventsApi.getStructure(eventId);
      if (backendStructure && Array.isArray(backendStructure)) {
        return backendStructure;
      }
    } catch (e) {
      console.warn('[eventsService.getStructure] Backend endpoint error, tentando RPC fallback:', e);
    }

    try {
      const { data, error } = await supabase.rpc('get_event_structure_v2_json', {
        p_event_id: eventId,
      });
      if (!error && data) {
        return data;
      }
    } catch (e) {
      console.warn('[eventsService.getStructure] RPC fallback:', e);
    }

    // Fallback: Query batch relacional
    const { data: days, error: daysErr } = await supabase
      .from('event_days')
      .select('id, event_id, day_number, label, scheduled_at, notes')
      .eq('event_id', eventId)
      .order('day_number');

    if (daysErr || !days || days.length === 0) return [];

    const dayIds = days.map(d => d.id);
    const { data: races } = await supabase
      .from('races')
      .select(`
        *,
        circuit_layouts (
          id, layout_name, map_url,
          circuit_game_configs (
            id,
            circuits (id, name, country, flag_emoji, image_url, logo_url)
          )
        ),
        cars (id, manufacturer, model, image_url, brand_logo_url)
      `)
      .in('event_day_id', dayIds)
      .order('race_number');

    const racesList = races || [];
    const raceIds = racesList.map(r => r.id);

    let sessionsList: any[] = [];
    if (raceIds.length > 0) {
      const { data: sess } = await supabase
        .from('sessions')
        .select('id, race_id, session_type, session_number, duration_minutes, label, status')
        .in('race_id', raceIds)
        .order('session_number');
      sessionsList = sess || [];
    }

    const sessionIds = sessionsList.map(s => s.id);
    let splitsList: any[] = [];
    if (sessionIds.length > 0) {
      const { data: sp } = await supabase
        .from('session_splits')
        .select('id, session_id, split_number, label, max_pilots')
        .in('session_id', sessionIds)
        .order('split_number');
      splitsList = sp || [];
    }

    const splitIds = splitsList.map(sp => sp.id);
    let entriesList: any[] = [];
    if (splitIds.length > 0) {
      const { data: ent } = await supabase
        .from('session_entries')
        .select(`
          *,
          users (display_name, username, avatar_url, current_ers, country_code)
        `)
        .in('split_id', splitIds);
      entriesList = ent || [];
    }

    // Monta árvore em memória
    const entriesBySplit: Record<string, any[]> = {};
    for (const entry of entriesList) {
      if (!entriesBySplit[entry.split_id]) entriesBySplit[entry.split_id] = [];
      entriesBySplit[entry.split_id].push(entry);
    }

    const splitsBySession: Record<string, any[]> = {};
    for (const split of splitsList) {
      split.entries = entriesBySplit[split.id] || [];
      if (!splitsBySession[split.session_id]) splitsBySession[split.session_id] = [];
      splitsBySession[split.session_id].push(split);
    }

    const sessionsByRace: Record<string, any[]> = {};
    for (const sess of sessionsList) {
      sess.splits = splitsBySession[sess.id] || [];
      if (!sessionsByRace[sess.race_id]) sessionsByRace[sess.race_id] = [];
      sessionsByRace[sess.race_id].push(sess);
    }

    const racesByDay: Record<string, any[]> = {};
    for (const race of racesList) {
      race.sessions = sessionsByRace[race.id] || [];
      if (!racesByDay[race.event_day_id]) racesByDay[race.event_day_id] = [];
      racesByDay[race.event_day_id].push(race);
    }

    return days.map(d => ({
      ...d,
      races: racesByDay[d.id] || [],
    }));
  },

  /**
   * Retorna os inscritos do evento.
   */
  async getRegistrations(eventId: string, includeReserves = false): Promise<any[]> {
    try {
      const { eventsApi } = await import('../lib/api');
      const res = await eventsApi.registrations(eventId, includeReserves);
      if (res && Array.isArray(res)) return res;
    } catch (e) {
      console.warn('[eventsService.getRegistrations] Backend endpoint error, tentando fallback:', e);
    }

    let query = supabase
      .from('event_registrations')
      .select(`
        *,
        users (id, username, display_name, current_ers, country_code, is_active, steam_id),
        cars (manufacturer, model),
        event_categories (id, name, tag, color_hex)
      `)
      .eq('event_id', eventId);

    if (!includeReserves) {
      query = query.or('status.eq.registered,status.is.null');
    } else {
      query = query.or('status.eq.registered,status.eq.reserve,status.is.null');
    }

    const { data, error } = await query.order('created_at', { ascending: true });

    if (error) {
      throw new Error(error.message);
    }

    return (data || []).filter(r => !r.users || r.users.is_active !== false);
  },

  /**
   * Inscreve o piloto no evento.
   * Tenta primeiro a API backend (FastAPI com service_role).
   * Caso o backend esteja offline ou indisponível, executa fallback via Supabase JS
   * mapeando o piloto corretamente para evitar erros de RLS.
   */
  async register(eventId: string, body: EventRegisterPayload = {}): Promise<any> {
    try {
      const { default: api } = await import('../lib/api');
      return await api.post<any>(`/events/${eventId}/register`, body);
    } catch (backendErr: any) {
      console.warn('[eventsService.register] Erro no backend, tentando fallback direto via Supabase:', backendErr);
      
      // Se o backend retornou um erro de regra de negócio (HTTP 4xx como vagas esgotadas ou requisitos), propaga o erro original
      if (backendErr?.status && backendErr.status >= 400 && backendErr.status < 500 && backendErr.status !== 404) {
        throw backendErr;
      }

      const authUser = await getAuthenticatedUser();
      if (!authUser) {
        throw new Error('Usuário não autenticado.');
      }

      const { data: userProfile } = await supabase
        .from('users')
        .select('id, supabase_uid, is_active, steam_id, steam_verified')
        .or(`id.eq.${authUser.id},supabase_uid.eq.${authUser.id}`)
        .maybeSingle();

      const realPilotId = userProfile?.id || authUser.id;

      if (userProfile?.is_active === false) {
        throw new Error('Sua conta está inativada. Reative-a nas configurações de perfil.');
      }

      const { data: event, error: eventErr } = await supabase
        .from('events')
        .select('*, leagues(id, slug, name), games(id, slug, short_name, name)')
        .eq('id', eventId)
        .single();

      if (eventErr || !event) {
        throw new Error('Evento não encontrado.');
      }

      // Eventos pagos: NUNCA registrar direto via Supabase.
      // O fallback bypassaria a cobrança de marbles e, sem sessão Supabase
      // (auth por cookie HttpOnly), falharia com erro de RLS no insert.
      if (event.is_paid && (event.entry_fee_tokens || 0) > 0) {
        throw new Error(
          backendErr?.detail
          || backendErr?.message
          || 'Não foi possível concluir a inscrição. Tente novamente em instantes.',
        );
      }

      const gameSlug = event.games?.slug;
      const leagueSlug = event.leagues?.slug;
      if (gameSlug === 'ams2' && leagueSlug === 'e-mss') {
        if (!userProfile?.steam_id || !userProfile?.steam_verified) {
          throw new Error('Você precisa vincular sua conta Steam nas configurações de perfil para se inscrever em eventos AMS2 da e-MSS.');
        }
      }

      if (event.registrations_locked) {
        throw new Error('As inscrições estão temporariamente bloqueadas pelo organizador.');
      }

      if (!['published', 'in_progress'].includes(event.status)) {
        throw new Error('Inscrições fechadas para este evento. O evento deve estar publicado.');
      }

      const { data: existing } = await supabase
        .from('event_registrations')
        .select('id')
        .eq('event_id', eventId)
        .or(`pilot_id.eq.${realPilotId},pilot_id.eq.${authUser.id}`);

      if (existing && existing.length > 0) {
        throw new Error('Você já está inscrito neste evento.');
      }

      const { count: activeCount } = await supabase
        .from('event_registrations')
        .select('id', { count: 'exact', head: true })
        .eq('event_id', eventId)
        .eq('status', 'registered');

      let isReserve = false;
      const currentActive = activeCount || 0;

      if (event.registration_limit && currentActive >= event.registration_limit) {
        if (event.allow_reserves) {
          isReserve = true;
        } else {
          throw new Error('Limite de inscrições atingido.');
        }
      }

      const { data: teamMem } = await supabase
        .from('team_memberships')
        .select('team_id')
        .or(`user_id.eq.${realPilotId},user_id.eq.${authUser.id}`)
        .eq('is_active', true)
        .limit(1);

      const activeTeamId = teamMem && teamMem.length > 0 ? teamMem[0].team_id : null;

      const payload: any = {
        event_id: eventId,
        pilot_id: realPilotId,
        team_id: activeTeamId,
        status: isReserve ? 'reserve' : 'registered',
        reserve_since: isReserve ? new Date().toISOString() : null,
        car_id: body.car_id || null,
        car_name: body.car_name || null,
        category_name: body.category_name || null,
        event_category_id: body.event_category_id || null,
      };

      const { data: inserted, error: insertErr } = await supabase
        .from('event_registrations')
        .insert(payload)
        .select()
        .single();

      if (insertErr) {
        console.error('[eventsService.register] Erro no insert do Supabase:', insertErr);
        throw new Error(insertErr.message || backendErr?.message || 'Erro ao realizar inscrição.');
      }

      if (event.game_id && event.category_id) {
        try {
          await supabase.rpc('increment_mrci_inscription', {
            p_pilot_id: realPilotId,
            p_game_id: event.game_id,
            p_category_id: event.category_id,
          });
        } catch (e) {
          console.warn('MRCI increment error:', e);
        }
      }

      return {
        message: isReserve ? 'Inscrição realizada como piloto reserva!' : 'Inscrição realizada com sucesso!',
        is_reserve: isReserve,
        registration: inserted,
      };
    }
  },

  /**
   * Cancela a inscrição do piloto logado.
   * Delega ao backend (Python/FastAPI) que usa service_role e autentica via cookies HttpOnly.
   */
  async unregister(eventId: string): Promise<any> {
    const { default: api } = await import('../lib/api');
    try {
      return await api.delete<any>(`/events/${eventId}/register`);
    } catch (err: any) {
      const msg = err?.detail || err?.message || 'Erro ao cancelar inscrição.';
      throw new Error(msg);
    }
  },

  /**
   * Atualiza dados de um evento (ex: status, bloqueio de inscrições, slots por split, etc.).
   * Tenta primeiro a API backend (FastAPI / service_role) para autorizar organizadores/co-organizadores
   * e recalcular o status do evento de forma atômica.
   */
  async update(eventId: string, payload: Record<string, any>): Promise<any> {
    try {
      const { eventsApi } = await import('../lib/api');
      return await eventsApi.update(eventId, payload);
    } catch (backendErr: any) {
      console.warn('[eventsService.update] API backend update error, tentando fallback via Supabase:', backendErr);

      // Se o backend retornou erro de validação/permissão (HTTP 400 ou 403), propaga o erro original
      if (backendErr?.status && backendErr.status >= 400 && backendErr.status < 500) {
        throw backendErr;
      }

      const { data, error } = await supabase
        .from('events')
        .update(payload)
        .eq('id', eventId)
        .select()
        .single();

      if (error) {
        throw new Error(error.message || backendErr?.message);
      }

      // Capacidade alterada ou fila de reserva (re)ativada → reequilibrar reservas
      if ('registration_limit' in payload || 'allow_reserves' in payload) {
        const rebalance = await rebalanceReservesFallback(eventId);
        return { ...data, reserve_rebalance: rebalance };
      }
      return data;
    }
  },

  /**
   * Exclui um evento e seus dados associados.
   */
  async delete(eventId: string): Promise<any> {
    try {
      const { eventsApi } = await import('../lib/api');
      return await eventsApi.delete(eventId);
    } catch (backendErr: any) {
      console.warn('[eventsService.delete] API backend delete error, tentando fallback:', backendErr);
      if (backendErr?.status && backendErr.status >= 400 && backendErr.status < 500) {
        throw backendErr;
      }
    }

    const { data: event } = await supabase
      .from('events')
      .select('status')
      .eq('id', eventId)
      .single();

    if (event && ['finished', 'in_progress'].includes(event.status)) {
      throw new Error('Não é possível excluir eventos em andamento ou finalizados.');
    }

    await supabase.from('event_stewards').delete().eq('event_id', eventId);
    await supabase.from('event_teams').delete().eq('event_id', eventId);
    await supabase.from('event_registrations').delete().eq('event_id', eventId);

    const { data: days } = await supabase.from('event_days').select('id').eq('event_id', eventId);
    if (days && days.length > 0) {
      const dayIds = days.map(d => d.id);
      const { data: races } = await supabase.from('races').select('id').in('event_day_id', dayIds);
      if (races && races.length > 0) {
        const raceIds = races.map(r => r.id);
        const { data: sessions } = await supabase.from('sessions').select('id').in('race_id', raceIds);
        if (sessions && sessions.length > 0) {
          const sessionIds = sessions.map(s => s.id);
          await supabase.from('session_entries').delete().in('session_id', sessionIds);
          await supabase.from('session_splits').delete().in('session_id', sessionIds);
          await supabase.from('session_results').delete().in('session_id', sessionIds);
        }
        await supabase.from('sessions').delete().in('race_id', raceIds);
        await supabase.from('races').delete().in('id', raceIds);
      }
      await supabase.from('event_days').delete().in('id', dayIds);
    }

    const { error } = await supabase.from('events').delete().eq('id', eventId);
    if (error) {
      throw new Error(error.message);
    }
    return { message: 'Evento excluído com sucesso.' };
  },

  /**
   * Gera splits para o evento.
   */
  async generateSplits(eventId: string): Promise<any> {
    try {
      const { eventsApi } = await import('../lib/api');
      return await eventsApi.generateSplits(eventId);
    } catch (backendErr: any) {
      console.warn('[eventsService.generateSplits] API backend error, tentando fallback:', backendErr);
      if (backendErr?.status && backendErr.status >= 400 && backendErr.status < 500) {
        throw backendErr;
      }
    }

    const { data: event } = await supabase
      .from('events')
      .select('id, slots_per_split, game_id, category_id')
      .eq('id', eventId)
      .single();
    if (!event) throw new Error('Evento não encontrado.');

    const slotsPerSplit = event.slots_per_split || 20;

    const { data: regs } = await supabase
      .from('event_registrations')
      .select('pilot_id, category_name, users(id, current_ers)')
      .eq('event_id', eventId)
      .eq('status', 'registered');

    if (!regs || regs.length === 0) {
      return { message: 'Nenhum piloto inscrito para gerar splits.' };
    }

    // Excluir categorias de transmissão (igual ao backend)
    const gridRegs = regs.filter((r: any) => !BROADCASTER_CATS.has((r.category_name || '').toUpperCase().trim()));
    if (gridRegs.length === 0) {
      return { message: 'Nenhum piloto de grid inscrito para gerar splits.' };
    }

    // Resolver macro categoria (carteira) para buscar o eRS correto
    let macroCatId: string | null = event.category_id || null;
    if (macroCatId) {
      const { data: cat } = await supabase
        .from('platform_categories')
        .select('id, parent_id')
        .eq('id', macroCatId)
        .single();
      if (cat) macroCatId = cat.parent_id || cat.id;
    }

    // Enriquecer eRS via global_pilot_ranking (fallback pilot_experience), padrão 1200
    const rankingMap = new Map<string, number>();
    const pidList = gridRegs.map((r: any) => r.pilot_id);
    try {
      let rankQuery = supabase
        .from('global_pilot_ranking')
        .select('pilot_id, current_ers')
        .in('pilot_id', pidList)
        .eq('game_id', event.game_id);
      if (macroCatId) rankQuery = rankQuery.eq('category_id', macroCatId);
      const { data: rankRows } = await rankQuery;
      for (const row of rankRows || []) {
        if (row.pilot_id && row.current_ers != null) rankingMap.set(row.pilot_id, parseFloat(row.current_ers));
      }
      const missing = pidList.filter((pid: string) => !rankingMap.has(pid));
      if (missing.length > 0 && macroCatId) {
        const { data: peRows } = await supabase
          .from('pilot_experience')
          .select('pilot_id, current_ers')
          .in('pilot_id', missing)
          .eq('game_id', event.game_id)
          .eq('category_id', macroCatId);
        for (const row of peRows || []) {
          if (row.pilot_id && row.current_ers != null) rankingMap.set(row.pilot_id, parseFloat(row.current_ers));
        }
      }
    } catch (e) {
      console.warn('[generateSplits:fallback] falha ao consultar ranking, usando eRS local/padrão:', e);
    }

    const ersOf = (r: any): number => {
      if (rankingMap.has(r.pilot_id)) return rankingMap.get(r.pilot_id)!;
      const local = r.users?.current_ers;
      return local != null ? Number(local) : DEFAULT_ERS;
    };

    const sortedRegs = [...gridRegs].sort((a: any, b: any) => ersOf(b) - ersOf(a));

    const pilotIds = sortedRegs.map((r: any) => r.pilot_id);
    const totalSplits = Math.ceil(pilotIds.length / slotsPerSplit);
    const splitSizes = computeSplitSizes(pilotIds.length, totalSplits);

    // Busca sessões com data (igual ao backend: protege passadas/finalizadas)
    const { data: days } = await supabase.from('event_days').select('id, scheduled_date').eq('event_id', eventId);
    if (!days || days.length === 0) return { message: 'Nenhum dia configurado.' };
    const dayMap = new Map(days.map((d: any) => [d.id, d.scheduled_date]));

    const { data: races } = await supabase.from('races').select('id, event_day_id').in('event_day_id', days.map((d: any) => d.id));
    if (!races || races.length === 0) return { message: 'Nenhuma corrida configurada.' };
    const raceMap = new Map(races.map((r: any) => [r.id, dayMap.get(r.event_day_id) || '1970-01-01']));

    const { data: sessions } = await supabase
      .from('sessions')
      .select('id, race_id, session_type, is_finished')
      .in('race_id', races.map((r: any) => r.id));
    if (!sessions || sessions.length === 0) return { message: 'Nenhuma sessão configurada.' };

    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

    for (const sess of sessions) {
      // Qualificação nunca tem splits
      if (sess.session_type === 'qualifying') {
        await supabase.from('session_entries').update({ split_id: null }).eq('session_id', sess.id);
        await supabase.from('session_splits').delete().eq('session_id', sess.id);
        continue;
      }

      // Sessões passadas/finalizadas não são tocadas
      const sessionDate = raceMap.get(sess.race_id) || '1970-01-01';
      if (sessionDate < todayStr || sess.is_finished) continue;

      await supabase.from('session_entries').update({ split_id: null }).eq('session_id', sess.id);
      await supabase.from('session_splits').delete().eq('session_id', sess.id);
      if (pilotIds.length > 0) {
        await supabase
          .from('session_entries')
          .delete()
          .eq('session_id', sess.id)
          .not('pilot_id', 'in', `(${pilotIds.join(',')})`);
      }

      const splitRecords = splitSizes.map((size, i) => ({
        session_id: sess.id,
        split_number: i + 1,
        label: `Split ${i + 1}`,
        max_pilots: size,
      }));

      const { data: insertedSplits } = await supabase.from('session_splits').insert(splitRecords).select();
      if (!insertedSplits) continue;

      const { data: existingEntries } = await supabase
        .from('session_entries')
        .select('id, pilot_id')
        .eq('session_id', sess.id);
      const existingMap = new Map<string, string>(
        (existingEntries || []).map((e: any) => [e.pilot_id, e.id])
      );

      let currentIdx = 0;
      for (let i = 0; i < totalSplits; i++) {
        const splitId = insertedSplits[i]?.id;
        const chunk = pilotIds.slice(currentIdx, currentIdx + splitSizes[i]);
        currentIdx += splitSizes[i];

        for (const pid of chunk) {
          const existingId = existingMap.get(pid);
          if (existingId) {
            await supabase
              .from('session_entries')
              .update({ split_id: splitId, status: 'registered' })
              .eq('id', existingId);
          } else {
            await supabase.from('session_entries').insert({
              session_id: sess.id,
              pilot_id: pid,
              split_id: splitId,
              status: 'registered',
            });
          }
        }
      }
    }

    return {
      status: 'success',
      message: `${totalSplits} splits gerados com sucesso!`,
      split_sizes: splitSizes,
    };
  },

  /**
   * Envia anúncio/notificação para inscritos do evento.
   * Somente via backend — o endpoint valida ownership (criador/dono da liga/
   * co-organizador/admin). Sem fallbacks que contornam a autorização.
   */
  async sendAnnouncement(eventId: string, message: string): Promise<any> {
    const { eventsApi } = await import('../lib/api');
    return await eventsApi.sendAnnouncement(eventId, message);
  },

  /**
   * Standings do Evento.
   */
  async getStandings(eventId: string): Promise<any[]> {
    try {
      const { eventsApi } = await import('../lib/api');
      const res = await eventsApi.getStandings(eventId);
      if (res && Array.isArray(res)) return res;
    } catch (e) {
      console.warn('[eventsService.getStandings] Backend error, tentando fallback:', e);
    }

    const { data: event } = await supabase
      .from('events')
      .select('id, scoring_settings, commission_settings, commission_enabled')
      .eq('id', eventId)
      .single();
    if (!event) return [];

    const { data: regs } = await supabase
      .from('event_registrations')
      .select(`
        pilot_id, event_team_id, event_teams(id, name, tag),
        event_category_id, event_categories(id, name, tag, color_hex),
        users(id, username, display_name, avatar_url, current_ers)
      `)
      .eq('event_id', eventId);

    const fallbackRegs = (regs || []).map(r => ({
      pilot_id: r.pilot_id,
      user: r.users,
      event_team_id: r.event_team_id,
      event_teams: r.event_teams,
      event_category_id: r.event_category_id,
      event_categories: r.event_categories,
      total_points: 0,
      base_points: 0,
      bonus_points: 0,
      penalty_points: 0,
      sessions: [],
    }));

    const { data: days } = await supabase.from('event_days').select('id').eq('event_id', eventId);
    if (!days || days.length === 0) return fallbackRegs;

    const { data: races } = await supabase.from('races').select('id').in('event_day_id', days.map(d => d.id));
    if (!races || races.length === 0) return fallbackRegs;

    const { data: sessions } = await supabase
      .from('sessions')
      .select('id, session_type')
      .in('race_id', races.map(r => r.id))
      .neq('session_type', 'qualifying');

    if (!sessions || sessions.length === 0) return fallbackRegs;

    const sessionIds = sessions.map(s => s.id);
    const { data: results } = await supabase
      .from('session_results')
      .select(`
        session_id, pilot_id, global_ers_points, position, status, is_pole, is_fastest_lap, extra_points_awarded,
        event_team_id, event_teams(id, name, tag),
        users(id, username, display_name, avatar_url, current_ers)
      `)
      .in('session_id', sessionIds);

    if (!results || results.length === 0) {
      return fallbackRegs;
    }

    const standingsMap: Record<string, any> = {};
    (regs || []).forEach(r => {
      standingsMap[r.pilot_id] = {
        pilot_id: r.pilot_id,
        user: r.users,
        event_team_id: r.event_team_id,
        event_teams: r.event_teams,
        event_category_id: r.event_category_id,
        event_categories: r.event_categories,
        total_points: 0,
        base_points: 0,
        bonus_points: 0,
        penalty_points: 0,
        sessions: [],
      };
    });

    const getBasePoints = (pos: number, sessionType: string = 'race') => {
      const templateName = event.scoring_template || 'ers';
      const customMap = event.scoring_settings?.custom_points || {};
      let pointsList = customMap[sessionType] || customMap['default'];
      if (!pointsList || pointsList.length === 0) {
        if (['f1', 'platform', 'gt', 'forza', 'forza_custom'].includes(templateName)) {
          pointsList = [25, 18, 15, 12, 10, 8, 6, 4, 2, 1];
        }
      }
      if (!pos || pos <= 0) return 0;
      if (pointsList && pos <= pointsList.length) {
        return Number(pointsList[pos - 1]) || 0;
      }
      return 0;
    };

    for (const r of results) {
      const pId = r.pilot_id;
      if (!standingsMap[pId]) {
        const regInfo = (regs || []).find(rg => rg.pilot_id === pId) || {};
        standingsMap[pId] = {
          pilot_id: pId,
          user: r.users || regInfo.users,
          event_team_id: regInfo.event_team_id || r.event_team_id,
          event_teams: regInfo.event_teams || r.event_teams,
          event_category_id: regInfo.event_category_id,
          event_categories: regInfo.event_categories,
          total_points: 0,
          base_points: 0,
          bonus_points: 0,
          penalty_points: 0,
          sessions: [],
        };
      } else if (!standingsMap[pId].event_team_id && (r.event_team_id || (regs || []).find(rg => rg.pilot_id === pId)?.event_team_id)) {
        const regInfo = (regs || []).find(rg => rg.pilot_id === pId) || {};
        standingsMap[pId].event_team_id = regInfo.event_team_id || r.event_team_id;
        standingsMap[pId].event_teams = regInfo.event_teams || r.event_teams;
      }

      const rawErs = r.global_ers_points;
      const rStatus = (r.status || 'registered').toLowerCase();

      let bonusPts = 0;
      if (rStatus !== 'dsq' && rStatus !== 'dns') {
        if (r.is_pole) bonusPts += Number(event.scoring_settings?.points_pole || 0);
        if (r.is_fastest_lap) bonusPts += Number(event.scoring_settings?.points_fastest_lap || 0);
        if (Array.isArray(r.extra_points_awarded)) {
          r.extra_points_awarded.forEach((item: any) => {
            bonusPts += Number(item.points || 0);
          });
        }
      }

      let basePts = 0;
      let totalPtsSessao = 0;

      if (rStatus === 'dsq' || rStatus === 'dns') {
        basePts = 0;
        bonusPts = 0;
        totalPtsSessao = 0;
      } else if (rStatus === 'dnf') {
        const commSettings = event.commission_settings || {};
        const dnfPointsEnabled = commSettings.dnf_points?.enabled;
        const dnfFixed = Number(commSettings.dnf_points?.fixed_value) || 0;
        if (dnfPointsEnabled) {
          basePts = getBasePoints(r.position, 'race');
          totalPtsSessao = basePts + bonusPts;
        } else if (dnfFixed > 0) {
          basePts = dnfFixed;
          totalPtsSessao = basePts + bonusPts;
        } else if (rawErs !== null && rawErs !== undefined && Number(rawErs) > 0) {
          totalPtsSessao = Number(rawErs);
          basePts = Math.max(0, totalPtsSessao - bonusPts);
        } else {
          basePts = 0;
          totalPtsSessao = bonusPts;
        }
      } else if (rawErs !== null && rawErs !== undefined) {
        totalPtsSessao = Number(rawErs);
        basePts = Math.max(0, totalPtsSessao - bonusPts);
      } else {
        basePts = getBasePoints(r.position, 'race');
        totalPtsSessao = basePts + bonusPts;
      }

      standingsMap[pId].base_points += basePts;
      standingsMap[pId].bonus_points += bonusPts;
      standingsMap[pId].total_points += totalPtsSessao;

      standingsMap[pId].sessions.push({
        session_id: r.session_id,
        position: r.position,
        status: r.status,
        base_points: basePts,
        bonus_points: bonusPts,
        total_points: totalPtsSessao,
        extra_points_awarded: r.extra_points_awarded || [],
        is_dropped: false,
        event_team_id: r.event_team_id || standingsMap[pId].event_team_id,
        event_teams: r.event_teams || standingsMap[pId].event_teams,
      });
    }

    const dropEnabled = !!event.scoring_settings?.drop_worst_race;
    const dropIncludesDns = !!event.scoring_settings?.drop_includes_dns;
    // Quantidade de piores resultados a descartar (1 a 3). Default 1 para
    // retrocompatibilidade com eventos antigos sem a chave.
    const rawDropCount = Number(event.scoring_settings?.drop_worst_count);
    const dropCount = Math.min(3, Math.max(1, Number.isFinite(rawDropCount) && rawDropCount ? rawDropCount : 1));

    if (dropEnabled) {
      Object.values(standingsMap).forEach(pilotData => {
        const sessList = pilotData.sessions;
        const eligible = dropIncludesDns ? sessList : sessList.filter((s: any) => s.status !== 'dns');
        // Regra estrita: descarta no máximo N, e apenas se houver N+1
        // resultados elegíveis (sempre deve restar ao menos 1 resultado).
        if (eligible.length > dropCount) {
          // sort estável → empates em pontuação mantêm a ordem cronológica.
          const worstSess = [...eligible]
            .sort((a: any, b: any) => a.total_points - b.total_points)
            .slice(0, dropCount);
          let totalDropped = 0;
          worstSess.forEach((s: any) => {
            s.is_dropped = true;
            totalDropped += s.total_points;
          });
          pilotData.total_points -= totalDropped;
          pilotData.dropped_points = totalDropped;
        }
      });
    }

    const standingsList = Object.values(standingsMap);
    standingsList.sort((a, b) => b.total_points - a.total_points);
    standingsList.forEach((item, idx) => {
      item.rank = idx + 1;
    });

    return standingsList;
  },

  // ── Carros & Categorias ──────────────────────────────────────────────────
  async listCars(eventId: string): Promise<any[]> {
    try {
      const { eventsApi } = await import('../lib/api');
      const res = await eventsApi.listCars(eventId);
      if (res && Array.isArray(res)) return res;
    } catch (e) {}

    const { data } = await supabase
      .from('event_cars')
      .select('*, cars(id, manufacturer, model, car_class, image_url, brand_logo_url, category_id, platform_categories(id, name))')
      .eq('event_id', eventId);
    return data || [];
  },

  async addCar(eventId: string, carId: string): Promise<any> {
    try {
      const { eventsApi } = await import('../lib/api');
      return await eventsApi.addCar(eventId, carId);
    } catch (err: any) {
      if (err?.status && err.status >= 400 && err.status < 500) throw err;
    }

    const { data, error } = await supabase.from('event_cars').insert({ event_id: eventId, car_id: carId }).select().single();
    if (error) throw new Error(error.message);
    return data;
  },

  async removeCar(eventId: string, carId: string): Promise<any> {
    try {
      const { eventsApi } = await import('../lib/api');
      return await eventsApi.removeCar(eventId, carId);
    } catch (err: any) {
      if (err?.status && err.status >= 400 && err.status < 500) throw err;
    }

    const { error } = await supabase.from('event_cars').delete().eq('event_id', eventId).eq('car_id', carId);
    if (error) throw new Error(error.message);
    return { status: 'success' };
  },

  async listCategories(eventId: string): Promise<any[]> {
    try {
      const { eventsApi } = await import('../lib/api');
      const res = await eventsApi.listCategories(eventId);
      if (res && Array.isArray(res)) return res;
    } catch (e) {}

    const { data } = await supabase
      .from('event_categories')
      .select('id, event_id, name, tag, color_hex, max_slots')
      .eq('event_id', eventId)
      .order('name');
    return data || [];
  },

  async createCategory(eventId: string, payload: any): Promise<any> {
    try {
      const { eventsApi } = await import('../lib/api');
      return await eventsApi.createCategory(eventId, payload);
    } catch (err: any) {
      if (err?.status && err.status >= 400 && err.status < 500) throw err;
    }

    const { data, error } = await supabase.from('event_categories').insert({ ...payload, event_id: eventId }).select().single();
    if (error) throw new Error(error.message);
    return data;
  },

  async updateCategory(eventId: string, categoryId: string, payload: any): Promise<any> {
    try {
      const { eventsApi } = await import('../lib/api');
      return await eventsApi.updateCategory(eventId, categoryId, payload);
    } catch (err: any) {
      if (err?.status && err.status >= 400 && err.status < 500) throw err;
    }

    const { data, error } = await supabase.from('event_categories').update(payload).eq('id', categoryId).eq('event_id', eventId).select().single();
    if (error) throw new Error(error.message);

    // Vagas da categoria alteradas → reequilibrar a fila de reserva do evento
    if ('max_slots' in payload) {
      const rebalance = await rebalanceReservesFallback(eventId, categoryId);
      return { ...data, reserve_rebalance: rebalance };
    }
    return data;
  },

  async deleteCategory(eventId: string, categoryId: string): Promise<any> {
    try {
      const { eventsApi } = await import('../lib/api');
      return await eventsApi.deleteCategory(eventId, categoryId);
    } catch (err: any) {
      if (err?.status && err.status >= 400 && err.status < 500) throw err;
    }

    const { error } = await supabase.from('event_categories').delete().eq('id', categoryId).eq('event_id', eventId);
    if (error) throw new Error(error.message);
    return { status: 'success' };
  },

  // ── Equipes Fictícias & Comissários ─────────────────────────────────────────
  async listTeams(eventId: string): Promise<any[]> {
    try {
      const { eventsApi } = await import('../lib/api');
      const res = await eventsApi.listTeams(eventId);
      if (res && Array.isArray(res)) return res;
    } catch (e) {}

    const { data } = await supabase
      .from('event_teams')
      .select('id, event_id, name, tag, car_id')
      .eq('event_id', eventId)
      .order('name');
    return data || [];
  },

  async createTeam(eventId: string, payload: any): Promise<any> {
    try {
      const { eventsApi } = await import('../lib/api');
      return await eventsApi.createTeam(eventId, payload);
    } catch (err: any) {
      if (err?.status && err.status >= 400 && err.status < 500) throw err;
    }

    const { data, error } = await supabase.from('event_teams').insert({ ...payload, event_id: eventId }).select().single();
    if (error) throw new Error(error.message);
    return data;
  },

  async updateTeam(eventId: string, teamId: string, payload: any): Promise<any> {
    try {
      const { eventsApi } = await import('../lib/api');
      return await eventsApi.updateTeam(eventId, teamId, payload);
    } catch (err: any) {
      if (err?.status && err.status >= 400 && err.status < 500) throw err;
    }

    const { data, error } = await supabase.from('event_teams').update(payload).eq('id', teamId).eq('event_id', eventId).select().single();
    if (error) throw new Error(error.message);
    return data;
  },

  async deleteTeam(eventId: string, teamId: string): Promise<any> {
    try {
      const { eventsApi } = await import('../lib/api');
      return await eventsApi.deleteTeam(eventId, teamId);
    } catch (err: any) {
      if (err?.status && err.status >= 400 && err.status < 500) throw err;
    }

    const { error } = await supabase.from('event_teams').delete().eq('id', teamId).eq('event_id', eventId);
    if (error) throw new Error(error.message);
    return { status: 'success' };
  },

  async listStewards(eventId: string): Promise<any[]> {
    try {
      const { eventsApi } = await import('../lib/api');
      const res = await eventsApi.listStewards(eventId);
      if (res && Array.isArray(res)) return res;
    } catch (e) {}

    const { data } = await supabase
      .from('event_stewards')
      .select('id, event_id, user_id, is_active, created_at, assigned_by, users!event_stewards_user_id_fkey(id, username, display_name, avatar_url)')
      .eq('event_id', eventId);
    return data || [];
  },

  async addSteward(eventId: string, userId: string): Promise<any> {
    // Escrita somente via backend — o endpoint valida a credencial (role 'steward')
    // do usuário designado. Sem fallback de insert direto.
    const { eventsApi } = await import('../lib/api');
    return await eventsApi.addSteward(eventId, userId);
  },

  async removeSteward(eventId: string, userId: string): Promise<any> {
    const { eventsApi } = await import('../lib/api');
    return await eventsApi.removeSteward(eventId, userId);
  },

  async listGlobalStewards(): Promise<any[]> {
    const { data } = await supabase
      .from('user_roles')
      .select('users!inner(id, username, display_name, avatar_url, current_ers, country_code), roles!inner(slug)')
      .eq('roles.slug', 'steward')
      .eq('is_active', true);
    if (data && data.length > 0) {
      const stewards = data.map((r: any) => r.users).filter(Boolean);
      const uniqueMap = new Map();
      stewards.forEach((s: any) => uniqueMap.set(s.id, s));
      return Array.from(uniqueMap.values());
    }
    return [];
  },

  async searchUsers(query: string, role?: string): Promise<any[]> {
    if (!query || query.trim().length === 0) return [];
    const q = query.trim();
    try {
      const { usersApi } = await import('../lib/api');
      const res = await usersApi.search(q, role);
      if (res && Array.isArray(res)) return res;
    } catch (e) {}

    // Fallback via Supabase: se houver role, restrinja aos usuários que a possuem ativa.
    if (role) {
      const { data: roleRows } = await supabase
        .from('user_roles')
        .select('user_id, roles!inner(slug)')
        .eq('roles.slug', role)
        .eq('is_active', true);
      const ids = (roleRows || []).map((r: any) => r.user_id);
      if (ids.length === 0) return [];
      const { data } = await supabase
        .from('users')
        .select('id, username, display_name, avatar_url, current_ers, country_code')
        .in('id', ids)
        .eq('is_active', true)
        .or(`display_name.ilike.%${q}%,username.ilike.%${q}%`)
        .limit(20);
      return data || [];
    }

    const { data } = await supabase
      .from('users')
      .select('id, username, display_name, avatar_url, current_ers, country_code')
      .or(`display_name.ilike.%${q}%,username.ilike.%${q}%`)
      .limit(20);
    return data || [];
  },

  async updateRegistration(eventId: string, registrationId: string, payload: RegistrationUpdatePayload): Promise<any> {
    try {
      const { eventsApi } = await import('../lib/api');
      return await eventsApi.updateRegistration(eventId, registrationId, payload);
    } catch (err: any) {
      if (err?.status && err.status >= 400 && err.status < 500) throw err;
    }

    const { data, error } = await supabase
      .from('event_registrations')
      .update(payload)
      .eq('id', registrationId)
      .eq('event_id', eventId)
      .select()
      .single();

    if (error) throw new Error(error.message);
    return data;
  },

  async removeRegistration(eventId: string, pilotId: string): Promise<any> {
    try {
      const { eventsApi } = await import('../lib/api');
      return await eventsApi.removeRegistration(eventId, pilotId);
    } catch (err: any) {
      if (err?.status && err.status >= 400 && err.status < 500) throw err;
    }

    const { error } = await supabase
      .from('event_registrations')
      .delete()
      .eq('event_id', eventId)
      .eq('pilot_id', pilotId);

    if (error) throw new Error(error.message);
    return { status: 'success' };
  },

  async activateReserve(eventId: string, registrationId: string, payload: ActivateReservePayload = {}): Promise<any> {
    try {
      const { eventsApi } = await import('../lib/api');
      return await eventsApi.activateReserve(eventId, registrationId, payload);
    } catch (err: any) {
      if (err?.status && err.status >= 400 && err.status < 500) throw err;
    }

    if (payload.replace_pilot_id) {
      await supabase
        .from('event_registrations')
        .update({ status: 'reserve', reserve_since: new Date().toISOString() })
        .eq('event_id', eventId)
        .eq('pilot_id', payload.replace_pilot_id);
    }

    const { data, error } = await supabase
      .from('event_registrations')
      .update({ status: 'registered', reserve_since: null })
      .eq('id', registrationId)
      .select()
      .single();

    if (error) throw new Error(error.message);

    // Mesmo comportamento do backend: reequilibra a fila ao final da troca,
    // sem re-promover o titular que acabou de sair.
    await rebalanceReservesFallback(
      eventId,
      undefined,
      payload.replace_pilot_id ? [payload.replace_pilot_id] : []
    );

    return data;
  },

  async getServers(eventId: string): Promise<any[]> {
    try {
      const { eventsApi } = await import('../lib/api');
      const res = await eventsApi.getServers(eventId);
      if (res && Array.isArray(res)) return res;
    } catch (e) {}

    const { data } = await supabase
      .from('game_servers')
      .select('id, event_id, name, host, port, password, game_id, is_active')
      .eq('event_id', eventId)
      .eq('is_active', true);
    return data || [];
  },

  // ── AMS2 & Forza Configs ─────────────────────────────────────────────────
  async getAms2Config(eventId: string): Promise<any> {
    try {
      const { eventsApi } = await import('../lib/api');
      return await eventsApi.getAms2Config(eventId);
    } catch (e) {}

    const { data } = await supabase
      .from('ams2_event_configs')
      .select('id, event_id, server_name, password, car_class_id, max_players, race_laps, time_of_day, race_weather_slots, race_weather_slot_1, race_weather_slot_2, race_weather_slot_3, race_weather_slot_4, ambient_temp, track_temp, damage_setting, mechanical_failures, fuel_usage, tyre_wear, stability_control, anti_lock_brakes, traction_control, rolling_start, max_ai, ai_difficulty')
      .eq('event_id', eventId)
      .single();
    return data || null;
  },

  async saveAms2Config(eventId: string, payload: any): Promise<any> {
    try {
      const { eventsApi } = await import('../lib/api');
      return await eventsApi.saveAms2Config(eventId, payload);
    } catch (err: any) {
      if (err?.status && err.status >= 400 && err.status < 500) throw err;
    }

    const { data, error } = await supabase
      .from('ams2_event_configs')
      .upsert({ ...payload, event_id: eventId }, { onConflict: 'event_id' })
      .select()
      .single();
    if (error) throw new Error(error.message);
    return data;
  },

  async getAms2Races(eventId: string): Promise<any[]> {
    try {
      const { eventsApi } = await import('../lib/api');
      const res = await eventsApi.getAms2Races(eventId);
      if (res && Array.isArray(res)) return res;
    } catch (e) {}

    const { data } = await supabase
      .from('ams2_race_configs')
      .select('id, event_id, race_number, label, circuit_layout_id, car_class_id, race_laps, has_splits, slots_per_split, time_of_day, race_weather_slots, race_weather_slot_1, race_weather_slot_2, race_weather_slot_3, race_weather_slot_4, scheduled_at')
      .eq('event_id', eventId)
      .order('race_number');
    return data || [];
  },

  async saveAms2Race(eventId: string, race: any): Promise<any> {
    try {
      const { eventsApi } = await import('../lib/api');
      return await eventsApi.saveAms2Race(eventId, race);
    } catch (err: any) {
      if (err?.status && err.status >= 400 && err.status < 500) throw err;
    }

    if (race.id) {
      const { data, error } = await supabase.from('ams2_race_configs').update(race).eq('id', race.id).select().single();
      if (error) throw new Error(error.message);
      return data;
    } else {
      const { data, error } = await supabase.from('ams2_race_configs').insert({ ...race, event_id: eventId }).select().single();
      if (error) throw new Error(error.message);
      return data;
    }
  },

  async deleteAms2Race(eventId: string, raceId: string): Promise<any> {
    try {
      const { eventsApi } = await import('../lib/api');
      return await eventsApi.deleteAms2Race(eventId, raceId);
    } catch (err: any) {
      if (err?.status && err.status >= 400 && err.status < 500) throw err;
    }

    const { error } = await supabase.from('ams2_race_configs').delete().eq('id', raceId);
    if (error) throw new Error(error.message);
    return { status: 'success' };
  },

  async getForzaRaces(eventId: string): Promise<any[]> {
    try {
      const { eventsApi } = await import('../lib/api');
      const res = await eventsApi.getForzaRaces(eventId);
      if (res && Array.isArray(res)) return res;
    } catch (e) {}

    const { data } = await supabase
      .from('forza_race_configs')
      .select('id, event_id, race_number, label, circuit_name, car_name, weather_condition, time_of_day, race_laps, scheduled_at, notes')
      .eq('event_id', eventId)
      .order('race_number');
    return data || [];
  },

  async saveForzaRace(eventId: string, race: any): Promise<any> {
    try {
      const { eventsApi } = await import('../lib/api');
      return await eventsApi.saveForzaRace(eventId, race);
    } catch (err: any) {
      if (err?.status && err.status >= 400 && err.status < 500) throw err;
    }

    if (race.id) {
      const { data, error } = await supabase.from('forza_race_configs').update(race).eq('id', race.id).select().single();
      if (error) throw new Error(error.message);
      return data;
    } else {
      const { data, error } = await supabase.from('forza_race_configs').insert({ ...race, event_id: eventId }).select().single();
      if (error) throw new Error(error.message);
      return data;
    }
  },

  async deleteForzaRace(eventId: string, raceId: string): Promise<any> {
    try {
      const { eventsApi } = await import('../lib/api');
      return await eventsApi.deleteForzaRace(eventId, raceId);
    } catch (err: any) {
      if (err?.status && err.status >= 400 && err.status < 500) throw err;
    }

    const { error } = await supabase.from('forza_race_configs').delete().eq('id', raceId);
    if (error) throw new Error(error.message);
    return { status: 'success' };
  },

  // ── Catalog ─────────────────────────────────────────────────────────────────

  async listCatalogCircuits(gameId?: string): Promise<any[]> {
    try {
      const { eventsApi } = await import('../lib/api');
      const res = await eventsApi.catalog.getCircuits(gameId);
      if (res && Array.isArray(res) && res.length > 0) return res;
    } catch (e) {}

    let query = supabase
      .from('circuit_game_configs')
      .select(`
        id, game_id,
        circuit_layouts (
          id, layout_name, map_url, is_default,
          circuits (id, name, country, flag_emoji, image_url, logo_url)
        )
      `);
    if (gameId) {
      query = query.eq('game_id', gameId);
    }
    const { data } = await query;
    const items: any[] = [];
    (data || []).forEach((cgc: any) => {
      const layouts = Array.isArray(cgc.circuit_layouts)
        ? cgc.circuit_layouts
        : cgc.circuit_layouts ? [cgc.circuit_layouts] : [];

      layouts.forEach((layout: any) => {
        const circuit = layout?.circuits || {};
        items.push({
          id: layout.id,
          layout_id: layout.id,
          layout_name: layout.layout_name,
          map_url: layout.map_url,
          is_default: layout.is_default,
          circuit_id: circuit.id,
          name: circuit.name,
          country: circuit.country,
          flag_emoji: circuit.flag_emoji,
          image_url: circuit.image_url,
          logo_url: circuit.logo_url,
          circuit_game_config_id: cgc.id,
          game_id: cgc.game_id,
        });
      });
    });

    if (items.length === 0 || !gameId) {
      const { data: directCircuits } = await supabase
        .from('circuits')
        .select('id, name, country, flag_emoji, image_url, logo_url')
        .eq('is_active', true);

      (directCircuits || []).forEach((c: any) => {
        if (!items.some(item => item.circuit_id === c.id || item.name === c.name)) {
          items.push({
            id: c.id,
            circuit_id: c.id,
            name: c.name,
            country: c.country,
            flag_emoji: c.flag_emoji,
            image_url: c.image_url,
            logo_url: c.logo_url,
          });
        }
      });
    }

    return items;
  },

  async listCatalogCars(gameId?: string, categoryId?: string): Promise<any[]> {
    try {
      const { eventsApi } = await import('../lib/api');
      const res = await eventsApi.catalog.getCars(gameId, categoryId);
      if (res && Array.isArray(res) && res.length > 0) return res;
    } catch (e) {}

    let query = supabase
      .from('cars')
      .select('id, manufacturer, model, car_class, image_url, brand_logo_url, category_id, game_id, platform_categories(id, name)')
      .eq('is_active', true);
    if (gameId) query = query.eq('game_id', gameId);
    if (categoryId) query = query.eq('category_id', categoryId);
    const { data } = await query.order('manufacturer').order('model');
    return data || [];
  },

  // ── AMS2 Catalog (classes & vehicles from Supabase tables) ──────────────────

  async getAms2CatalogClasses(): Promise<any[]> {
    try {
      const { eventsApi } = await import('../lib/api');
      const res = await eventsApi.catalogAms2.getClasses();
      if (res && Array.isArray(res) && res.length > 0) return res;
    } catch (e) {}

    const { data } = await supabase
      .from('ams2_catalog_classes')
      .select('id, name, server_id')
      .eq('is_active', true)
      .order('name');
    return data || [];
  },

  async getAms2CatalogVehicles(classServerId?: number): Promise<any[]> {
    try {
      const { eventsApi } = await import('../lib/api');
      const res = await eventsApi.catalogAms2.getVehicles(classServerId);
      if (res && Array.isArray(res) && res.length > 0) return res;
    } catch (e) {}

    let query = supabase
      .from('ams2_catalog_vehicles')
      .select(`
        id, name, server_id, class_server_id, is_active,
        ams2_catalog_classes!ams2_catalog_vehicles_class_server_id_fkey(id, name, server_id)
      `)
      .eq('is_active', true);
    if (classServerId !== undefined) {
      query = query.eq('class_server_id', classServerId);
    }
    const { data } = await query.order('name');
    return (data || []).map((v: any) => ({
      ...v,
      class_name: v.ams2_catalog_classes?.name,
    }));
  },

  /**
   * Recordes de volta por etapa — fonte `session_laps` (preenchido pelo import
   * de resultados e pela confirmação de registro de telemetria).
   *
   * Busca também as parciais (`sector*_ms`) e o número da volta: sem elas o
   * Pit Lane não tem base para setor ideal / volta ótima e só consegue exibir
   * "—". A RPC `get_event_lap_records_v2` nunca foi aplicada em produção
   * (consta em sql/migrations/20260926_egress_and_query_optimization.sql, sem
   * registro em supabase_migrations), por isso a consulta direta é o caminho
   * real e não há RPC a chamar aqui.
   */
  async getLapRecordsByEvent(eventId: string): Promise<any[]> {
    const { data: days } = await supabase.from('event_days').select('id').eq('event_id', eventId);
    if (!days || days.length === 0) return [];

    const { data: races } = await supabase.from('races').select('id').in('event_day_id', days.map(d => d.id));
    if (!races || races.length === 0) return [];

    const { data: sessions } = await supabase.from('sessions').select('id').in('race_id', races.map(r => r.id));
    if (!sessions || sessions.length === 0) return [];

    const { data: laps } = await supabase
      .from('session_laps')
      .select('id, participant_steam_id, participant_name, lap_time_ms, lap_number, sector1_ms, sector2_ms, sector3_ms, stage_key, pilot_id, track_name, category_name, car_model')
      .in('session_id', sessions.map(s => s.id))
      .gt('lap_time_ms', 0)
      .order('lap_time_ms', { ascending: true })
      .limit(200);

    if (!laps || laps.length === 0) return [];

    const pilotIds = Array.from(new Set(laps.map(l => l.pilot_id).filter(Boolean)));
    let usersMap: Record<string, any> = {};
    if (pilotIds.length > 0) {
      const { data: users } = await supabase.from('users').select('id, display_name, username, current_ers, country_code').in('id', pilotIds);
      if (users) {
        usersMap = users.reduce((acc: any, u: any) => {
          acc[u.id] = u;
          return acc;
        }, {});
      }
    }

    const seenPilots = new Set<string>();
    const uniqueLaps: any[] = [];

    for (const lap of laps) {
      const key = lap.pilot_id || lap.participant_steam_id;
      if (!seenPilots.has(key)) {
        seenPilots.add(key);
        const ms = lap.lap_time_ms;
        const mins = Math.floor(ms / 60000);
        const secs = Math.floor((ms % 60000) / 1000);
        const millis = ms % 1000;
        const timeStr = `${mins}:${secs.toString().padStart(2, '0')}.${millis.toString().padStart(3, '0')}`;

        uniqueLaps.push({
          id: lap.id,
          pilot_id: lap.pilot_id,
          participant_steam_id: lap.participant_steam_id,
          participant_name: lap.participant_name,
          best_lap_ms: ms,
          best_lap_str: timeStr,
          lap_number: lap.lap_number ?? null,
          sector1_ms: lap.sector1_ms ?? null,
          sector2_ms: lap.sector2_ms ?? null,
          sector3_ms: lap.sector3_ms ?? null,
          stage_key: lap.stage_key,
          track_name: lap.track_name,
          category_name: lap.category_name,
          car_model: lap.car_model,
          users: lap.pilot_id ? usersMap[lap.pilot_id] : null,
        });
      }
    }

    return uniqueLaps;
  },

  async getLeagueEventDaysBatch(eventIds: string[]): Promise<Record<string, any[]>> {
    if (!eventIds || eventIds.length === 0) return {};
    try {
      const { data, error } = await supabase.rpc('get_league_event_days_batch', {
        p_event_ids: eventIds,
      });
      if (!error && Array.isArray(data)) {
        const map: Record<string, any[]> = {};
        for (const day of data) {
          if (!map[day.event_id]) map[day.event_id] = [];
          map[day.event_id].push(day);
        }
        return map;
      }
    } catch (e) {
      console.warn('[eventsService.getLeagueEventDaysBatch] RPC error, fallback:', e);
    }

    const { data: days } = await supabase
      .from('event_days')
      .select('id, event_id, day_number, label, scheduled_at, scheduled_date')
      .in('event_id', eventIds)
      .order('day_number');

    const map: Record<string, any[]> = {};
    if (days) {
      for (const d of days) {
        if (!map[d.event_id]) map[d.event_id] = [];
        map[d.event_id].push(d);
      }
    }
    return map;
  },

  async getLeagueEventsSummaryBatch(eventIds: string[], userId?: string): Promise<Record<string, { count: number; isRegistered: boolean }>> {
    if (!eventIds || eventIds.length === 0) return {};
    try {
      const { data, error } = await supabase.rpc('get_league_events_extra_summary', {
        p_event_ids: eventIds,
        p_user_id: userId || null,
      });
      if (!error && Array.isArray(data)) {
        const map: Record<string, { count: number; isRegistered: boolean }> = {};
        for (const item of data) {
          map[item.event_id] = {
            count: item.registration_count || 0,
            isRegistered: Boolean(item.is_registered),
          };
        }
        return map;
      }
    } catch (e) {
      console.warn('[eventsService.getLeagueEventsSummaryBatch] RPC error, fallback:', e);
    }

    const { data: regs } = await supabase
      .from('event_registrations')
      .select('event_id, pilot_id, status')
      .in('event_id', eventIds);

    const map: Record<string, { count: number; isRegistered: boolean }> = {};
    for (const id of eventIds) map[id] = { count: 0, isRegistered: false };

    if (regs) {
      for (const r of regs) {
        const isRegOrRes = !r.status || r.status === 'registered' || r.status === 'reserve';
        if (isRegOrRes && map[r.event_id]) {
          if (r.status === 'registered' || !r.status) {
            map[r.event_id].count += 1;
          }
          if (userId && (r.pilot_id === userId)) {
            map[r.event_id].isRegistered = true;
          }
        }
      }
    }
    return map;
  }
};
